/**
 * Pure helpers for the inventory-view "Custom Label Effects" rows. Mirrors the
 * rules api-svc enforces on view create/update (labelRuleValidators.ts, the
 * InventoryViewServiceImpl label-type check and the matchers' run-time guard)
 * so the form refuses the same combinations before the request is sent, with
 * the same wording. Framework-free so it can be unit-tested directly.
 */

export const INVENTORY_EFFECT_FORCE_ZERO_COST = 'force-zero-cost';
/**
 * A transaction tagged with the mapped `DateTime` slug label (e.g.
 * `acquired_at:2023-01-15T10:30:00Z`) has every lot it creates stamped with
 * that acquisition date for FIFO/LIFO ordering and the holding period.
 */
export const INVENTORY_EFFECT_OVERRIDE_ACQUISITION_DATE = 'override-acquisition-date';
/**
 * A transaction tagged with the mapped `ExchangeRate` slug label (e.g.
 * `tax_rate:COIN.10->2500`) is read by the cost-basis engine as if that asset
 * had been priced at the override when it was categorized: cost basis,
 * proceeds, trade allocation and fee capitalization follow. The books are
 * never touched; only the inventory view reads the override.
 */
export const INVENTORY_EFFECT_OVERRIDE_EXCHANGE_RATE = 'override-exchange-rate';

export interface InventoryEffectOption {
  label: string;
  value: string;
}

/**
 * Effects a label can carry. The generated client only knows ForceZeroCost
 * until the next swagger regen, so the values are plain strings here.
 */
export const INVENTORY_EFFECT_OPTIONS: InventoryEffectOption[] = [
  { label: 'Force Zero Cost', value: INVENTORY_EFFECT_FORCE_ZERO_COST },
  { label: 'Override Acquisition Date', value: INVENTORY_EFFECT_OVERRIDE_ACQUISITION_DATE },
  { label: 'Override Exchange Rate', value: INVENTORY_EFFECT_OVERRIDE_EXCHANGE_RATE },
];

/** Display names by effect value (settings view). */
export const INVENTORY_EFFECT_NAMES: { [effect: string]: string } = {
  [INVENTORY_EFFECT_FORCE_ZERO_COST]: 'Force Zero Cost',
  [INVENTORY_EFFECT_OVERRIDE_ACQUISITION_DATE]: 'Override Acquisition Date',
  [INVENTORY_EFFECT_OVERRIDE_EXCHANGE_RATE]: 'Override Exchange Rate',
};

/**
 * Effects whose label must be a slug template of one specific type, mirrored
 * from api-svc `InventoryViewServiceImpl.SLUG_EFFECT_LABEL_TYPES`. Each may be
 * carried by at most one label.
 */
const SLUG_EFFECT_TEMPLATE_TYPES: { [effect: string]: string } = {
  [INVENTORY_EFFECT_OVERRIDE_ACQUISITION_DATE]: 'DateTime',
  [INVENTORY_EFFECT_OVERRIDE_EXCHANGE_RATE]: 'ExchangeRate',
};

/** The slug template type an effect's label must have, or undefined for a flat-label effect. */
export function requiredSlugTypeFor(effect: string): string | undefined {
  return SLUG_EFFECT_TEMPLATE_TYPES[effect];
}

/** Engine floors, mirrored from api-svc `labelRuleValidators.ts` (acquisition date only; the exchange rate override has none). */
export const OVERRIDE_ACQUISITION_DATE_MIN_ENGINE_VERSION = 2.0;
export const OVERRIDE_ACQUISITION_DATE_MIN_ENGINE_VERSION_SPEC_ID = 3.1;

/** The subset of ApiSvcOrgLabelDefinition the effect rows look at. */
export interface LabelEffectLabelDef {
  labelId: string;
  name: string;
  enabled?: boolean;
  isDeleted?: boolean;
  allowSlugs?: boolean;
  slugType?: string;
}

export interface LabelEffectRow {
  labelId: string;
  effect: string;
}

export function isSpecificIdentificationStrategy(taxStrategy: string | undefined): boolean {
  return taxStrategy === 'SpecificIdentification' || taxStrategy === 'SelectedSpecificIdentification';
}

function isLive(def: LabelEffectLabelDef): boolean {
  return def.enabled !== false && !def.isDeleted;
}

function isSlugTemplateOf(def: LabelEffectLabelDef, slugType: string): boolean {
  return def.allowSlugs === true && def.slugType === slugType;
}

/** `a DateTime`, `an ExchangeRate` — same article rule as the api-svc message. */
function withArticle(slugType: string): string {
  return `${/^[AEIOU]/i.test(slugType) ? 'an' : 'a'} ${slugType}`;
}

/**
 * Labels eligible to carry the given effect. A slug-valued effect only accepts
 * an enabled slug template of its type (the engine parses the slug value as a
 * timestamp or as an asset price); any other effect, or no effect chosen yet,
 * keeps the full list the form always offered.
 */
export function labelOptionsForEffect<T extends LabelEffectLabelDef>(definitions: T[], effect: string): T[] {
  const required = requiredSlugTypeFor(effect);
  if (required === undefined) {
    return definitions;
  }
  return definitions.filter((d) => isLive(d) && isSlugTemplateOf(d, required));
}

/** Same text api-svc returns for an engine that cannot run the acquisition date effect. */
export function incompatibleEngineMessage(engineVersion: number, specificIdentification: boolean): string {
  const min = specificIdentification
    ? OVERRIDE_ACQUISITION_DATE_MIN_ENGINE_VERSION_SPEC_ID
    : OVERRIDE_ACQUISITION_DATE_MIN_ENGINE_VERSION;
  const withStrategy = specificIdentification ? ' with a specific-identification strategy' : '';
  return `Incompatible option: engine version ${engineVersion} cannot run with label effects: '${INVENTORY_EFFECT_OVERRIDE_ACQUISITION_DATE}' requires engine ${min} or later${withStrategy}`;
}

/**
 * Why one effect row cannot be submitted, or undefined when it can. A row with
 * no effect chosen yet is left to the "Effect is required" field validation.
 */
export function labelEffectRowError(
  row: LabelEffectRow,
  engineVersion: number,
  taxStrategy: string | undefined,
  definitions: LabelEffectLabelDef[]
): string | undefined {
  const required = requiredSlugTypeFor(row.effect);
  if (required === undefined) {
    return undefined;
  }
  if (row.effect === INVENTORY_EFFECT_OVERRIDE_ACQUISITION_DATE) {
    const specId = isSpecificIdentificationStrategy(taxStrategy);
    const min = specId
      ? OVERRIDE_ACQUISITION_DATE_MIN_ENGINE_VERSION_SPEC_ID
      : OVERRIDE_ACQUISITION_DATE_MIN_ENGINE_VERSION;
    if (engineVersion < min) {
      return incompatibleEngineMessage(engineVersion, specId);
    }
  }
  if (labelOptionsForEffect(definitions, row.effect).length === 0) {
    return `No enabled ${required} slug label template exists in this org. Create one under Administration › Custom Labels first.`;
  }
  if (!row.labelId) {
    return undefined;
  }
  const def = definitions.find((d) => d.labelId === row.labelId);
  if (def && !isLive(def)) {
    return `Label '${def.name || def.labelId}' is disabled`;
  }
  if (def && !isSlugTemplateOf(def, required)) {
    return `The '${row.effect}' effect requires ${withArticle(required)} slug label template; '${
      def.name || def.labelId
    }' is ${def.allowSlugs ? `${withArticle(def.slugType ?? '')} slug label` : 'not a slug label'}`;
  }
  return undefined;
}

/**
 * The first problem across all effect rows, or undefined when the rows can be
 * submitted. A lot has one acquisition date and the asset of an exchange rate
 * override lives in the slug value, so at most one label may carry each
 * slug-valued effect.
 */
export function labelEffectsError(
  rows: LabelEffectRow[],
  engineVersion: number,
  taxStrategy: string | undefined,
  definitions: LabelEffectLabelDef[]
): string | undefined {
  for (const effect of Object.keys(SLUG_EFFECT_TEMPLATE_TYPES)) {
    if (rows.filter((r) => r.effect === effect).length > 1) {
      return `Only one label may carry the '${effect}' effect`;
    }
  }
  for (const row of rows) {
    const err = labelEffectRowError(row, engineVersion, taxStrategy, definitions);
    if (err) {
      return err;
    }
  }
  return undefined;
}
